import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Camera, Search, Calendar, User, Download, X, Eye } from 'lucide-react';

const DeliveryProofsPage = () => {
  const { API_URL, getAuthHeader } = useAuth();
  const { t } = useLanguage();
  const [proofs, setProofs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [selectedDate, setSelectedDate] = useState('');
  const [viewImageUrl, setViewImageUrl] = useState('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, [selectedCustomer, selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/delivery-proofs`;
      const params = [];
      if (selectedCustomer && selectedCustomer !== 'all') {
        params.push(`customer_id=${selectedCustomer}`);
      }
      if (selectedDate) {
        params.push(`date=${selectedDate}`);
      }
      if (params.length > 0) {
        url += '?' + params.join('&');
      }

      const [proofsRes, customersRes] = await Promise.all([
        axios.get(url, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/customers`, { headers: getAuthHeader() })
      ]);

      setProofs(proofsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      console.error('Error fetching proofs:', error);
      toast.error(t('loadError') || 'خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const openImageView = (imageUrl) => {
    setViewImageUrl(imageUrl);
    setViewDialogOpen(true);
  };

  const downloadImage = (imageBase64, customerName, date) => {
    const link = document.createElement('a');
    link.href = imageBase64;
    link.download = `proof_${customerName}_${date}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
            <Camera className="w-7 h-7 text-amber-600" />
            {t('deliveryProofs') || 'إثبات الاستلام'}
          </h1>
          <p className="text-stone-500">{t('deliveryProofsDescription') || 'صور إثبات تسليم البضائع للزبائن'}</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm text-stone-600 mb-1 block">{t('customer') || 'الزبون'}</label>
              <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
                <SelectTrigger>
                  <SelectValue placeholder={t('allCustomers') || 'جميع الزبائن'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('allCustomers') || 'جميع الزبائن'}</SelectItem>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[180px]">
              <label className="text-sm text-stone-600 mb-1 block">{t('date') || 'التاريخ'}</label>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>
            <Button variant="outline" onClick={() => { setSelectedCustomer('all'); setSelectedDate(''); }}>
              <X className="w-4 h-4 me-1" />
              {t('clearFilters') || 'مسح الفلترة'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Proofs Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full"></div>
        </div>
      ) : proofs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <Camera className="w-16 h-16 mx-auto text-stone-300 mb-4" />
          <h3 className="text-xl font-bold text-stone-700">{t('noProofs') || 'لا توجد إثباتات'}</h3>
          <p className="text-stone-500">{t('noProofsDescription') || 'لم يتم رفع أي صور إثبات استلام بعد'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {proofs.map((proof) => (
            <Card key={proof.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="relative h-48 bg-stone-100">
                <img
                  src={proof.image_base64}
                  alt={`Proof for ${proof.customer_name}`}
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={() => openImageView(proof.image_base64)}
                />
                <div className="absolute top-2 right-2 flex gap-1">
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-8 w-8 p-0 bg-white/90"
                    onClick={() => openImageView(proof.image_base64)}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-8 w-8 p-0 bg-white/90"
                    onClick={() => downloadImage(proof.image_base64, proof.customer_name, proof.date)}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <CardContent className="p-3">
                <h4 className="font-bold text-stone-900 mb-1">{proof.customer_name}</h4>
                <div className="flex items-center gap-2 text-sm text-stone-500 mb-1">
                  <Calendar className="w-4 h-4" />
                  {proof.date}
                </div>
                <div className="flex items-center gap-2 text-sm text-stone-500">
                  <User className="w-4 h-4" />
                  {proof.uploaded_by}
                </div>
                {proof.notes && (
                  <p className="mt-2 text-sm text-stone-600 bg-stone-50 p-2 rounded">{proof.notes}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Image View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl p-0">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle>{t('viewImage') || 'عرض الصورة'}</DialogTitle>
          </DialogHeader>
          <div className="p-4">
            <img
              src={viewImageUrl}
              alt="Delivery proof"
              className="w-full max-h-[70vh] object-contain rounded-lg"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliveryProofsPage;
